import React, { useState } from 'react'
import { t } from 'i18next'
import { Add as AddIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { PrivatePage } from '../component/PagePrivate'
import { DataTable } from '../component/datatable'
import { useTableStyles } from '../component/datatable/hook/useStyles'
import { PopupPackage } from '../component/package/PopupPackage'
import { PopupWorkflow } from '../component/package/PopupWorkflow'
import sprite_ from '../sass/sprite.module.sass'
import { colorLightGreen, colorPrimary, colorSecondaryDark } from '../mui'
import { Workflow, Package } from '../store/package/reducer'
import { PopupAsk } from '../component/PopupAsk'
import { useDispatch, useSelector } from '../store'
import { deletePackage, deleteWorkflow } from '../store/package/actionCreator'
import { clearFieldError, showSnackbar } from '../store/common/actionCreator'
import { SvgIcon } from '../component/SvgIcon'
import { Filter, TableRef } from '../component/datatable/types'
import { PACKAGES_ACTIVE, PACKAGES_INCLUDES_AI, PACKAGES_TYPES, WORKFLOWS_TYPES } from '../enum/serviceTypesNames'
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar'
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import StarIcon from '@mui/icons-material/Star'

export function PackagePage() {
  const classes = useTableStyles()
  const dispatch = useDispatch()

  const { tables } = useSelector((state) => state.common)
  const workflowData: Workflow[] | null = tables.workflow
    ? tables.workflow.list
    : null
  const packageData: Package[] | null = tables.package?.list || null
  const refWorkflowTable = React.useRef<TableRef>(null)
  const refPackageTable = React.useRef<TableRef>(null)

  const [workflowCreateOpen, setWorkflowCreateOpen] = React.useState(false)
  const [workflowEdit, setWorkflowEdit] = React.useState<Workflow | null>(null)
  const [workflowDelete, setWorkflowDelete] = React.useState<number | null>(
    null,
  )
  const [packageCreateOpen, setPackageCreateOpen] = React.useState(false)
  const [packageEdit, setPackageEdit] = React.useState<Package | null>(null)
  const [packageDelete, setPackageDelete] = React.useState<number | null>(null)

  const affectingPackages: Package[] | null = React.useMemo(() => {
    if (packageData === null) return null

    return packageData.filter((el: Package) => {
      return el.lookups.some((l) => l.id === workflowDelete)
    })
  }, [workflowDelete, packageData])

  const tableHeaderPackage = [
    { key: 'title', text: t('package.table.title') },
    { key: 'subtitle', text: t('package.table.subtitle') },
    { key: 'price', text: t('package.table.price') },
    { key: 'features', text: t('package.table.features') },
    { key: 'actions', text: t('package.table.actions') },
  ]
  const tableHeaderLookup = [
    { key: 'title', text: t('package.table.title') },
    { key: 'subtitle', text: t('package.table.subtitle') },
    { key: 'price', text: t('package.table.price') },
    { key: 'sources', text: t('package.table.sources') },
    { key: 'actions', text: t('package.table.actions') },
  ]

  const handleClose = () => {
    setPackageCreateOpen(false)
    dispatch(clearFieldError)
  }

  const disableDisplay: string[] = ['quotaprofiler-periodic','quotaprofiler-monitoring','quotawebint-lookup','quotawebint-periodic']

  function openPayProProduct(id: number | null) {
    if (!id) return
    const url = `https://cc.payproglobal.com/Products/Edit/${id}?isWizard=False`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  function openPayProPrice(id: number | null) {
    if (!id) return
    const url = `https://cc.payproglobal.com/Products/EditPrices/${id}?isWizard=False`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const RenderLookupTableBody = (row: Workflow, idx: number) => (
    <TableRow
      key={row.id}
      className={idx % 2 !== 0 ? classes.rowEven : ''}
      hover
      onClick={() => setWorkflowEdit(row)}
    >
      <TableCell classes={classes}>
        <Typography variant="bold" noWrap>
          {row.title}
        </Typography>
      </TableCell>
      <TableCell classes={classes}>
        <Typography>{row.subTitle}</Typography>
      </TableCell>
      { row.type === 'webint' ? <TableCell classes={classes}></TableCell> : <TableCell classes={classes}>€ {row.price}</TableCell>}
      
      <TableCell classes={classes}>
        <Stack direction="row" spacing={1}>
          {row.sources?.map( (el) => (
             (!disableDisplay.includes(el) &&  
            <Box key={el} className={  (el === 'fb_api' || el === 'instagram_api' || el === 'x_api' 
              || el === 'linkedin_api' || el === 'vk_api' || el ===  'ok_api' || el === 'tiktok_api' || el === 'blsk_api') 
              ? sprite_['social__small_' + el.replace("_api","")] : sprite_['social__small_' + el] }  /> )
          ))}
        </Stack>
      </TableCell>
      <TableCell classes={classes}>
        <Tooltip title={<span>{t('common.delete')}</span>}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              setWorkflowDelete(row.id)
            }}
          >
            <SvgIcon
              name="delete"
              color={colorSecondaryDark}
              size={{ width: 18, height: 16 }}
            />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  )

  const RenderPackageTableBody = (row: Package, idx: number) => (
    <TableRow
      key={row.id}
      className={idx % 2 !== 0 ? classes.rowEven : ''}
      hover
      onClick={() => setPackageEdit(row)}
    >
      <TableCell classes={classes}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="bold">{row.title}</Typography>
          {row.type === 'personal' && row.productId === null && (
            <Tooltip title={<span>{'Enterprise'}</span>}>
              <Chip
                icon={<StarIcon sx={{ width: 14, height: 14 }} />}
                label="Enterprise"
                size="small"
                sx={{ color: '#FFF', backgroundColor: colorPrimary }}
              />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell classes={classes}>
        {row.subTitle?.type === 'text' ? (
          row.subTitle.context
        ) : (
          <Chip
            label={row.subTitle?.context}
            variant="outlined"
            size="small"
            sx={{
              color: row.type === 'custom' ? '#000' : '#FFF',
              backgroundColor:
                row.type === 'custom' ? colorLightGreen : colorPrimary,
            }}
          />
        )}
      </TableCell>
      <TableCell classes={classes}>
        <Typography noWrap>
          {row.type === 'custom' ? t('common.custom') : '€ ' + row.price}
        </Typography>
      </TableCell>
      <TableCell classes={classes}>
        {row.feature1?.title} • {row.feature2?.title} • {row.feature3?.title}{' '}
        {row.feature4?.title && '•' + row.feature4.title}
      </TableCell>
      <TableCell classes={classes}>
        <Tooltip title={<span>{t('common.delete')}</span>}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              setPackageDelete(row.id)
            }}
          >
            <SvgIcon
              name="delete"
              color={colorSecondaryDark}
              size={{ width: 18, height: 16 }}
            />
          </IconButton>
        </Tooltip>
        {row.description?.checkIsMonthlyName === false && (
          <Tooltip title={<span>{"Monthly package name does not match"}</span>}>
            <IconButton >
              <PermContactCalendarIcon color='warning' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProProduct(row.productId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {row.description?.checkIsAnnualName === false && (
          <Tooltip title={<span>{"Annual package name does not match"}</span>}>
            <IconButton >
              <PermContactCalendarIcon color='warning' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProProduct(row?.annualProductId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {row.description?.checkIsMonthlyActive === false && (
          <Tooltip title={<span>{"Monthly package is not active"}</span>}>
            <IconButton >
              <InsertInvitationIcon color='error' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProProduct(row?.productId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {row.description?.checkIsAnnualActive === false && (
          <Tooltip title={<span>{"Annual package is not active"}</span>}>
            <IconButton >
              <InsertInvitationIcon color='error' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProProduct(row?.annualProductId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {row.description?.checkIsMonthlyPrice === false && (
          <Tooltip title={<span>{"Monthly package price is not match"}</span>}>
            <IconButton >
              <MonetizationOnIcon color='error' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProPrice(row?.productId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {row.description?.checkIsAnnualPrice === false && (
          <Tooltip title={<span>{"Annual package price is not match"}</span>}>
            <IconButton >
              <MonetizationOnIcon color='error' sx={{ width: 18, height: 16 }}
                onClick={(e) => {
                  e.stopPropagation()
                  openPayProPrice(row?.annualProductId)
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {!(
          row.description?.checkIsMonthlyName === false ||
          row.description?.checkIsAnnualName === false ||
          row.description?.checkIsMonthlyActive === false ||
          row.description?.checkIsAnnualActive === false ||
          row.description?.checkIsMonthlyPrice === false ||
          row.description?.checkIsAnnualPrice === false
        ) && (
            <IconButton disabled>
              <CheckBoxIcon color="success" sx={{ width: 18, height: 16 }} />
            </IconButton>
          )}
      </TableCell>
    </TableRow>
  )

  function handlePackageCreate() {
    if (workflowData?.length === 0)
      dispatch(showSnackbar('error', 'Must be at least 1 search workflow'))
    else setPackageCreateOpen(true)
  }

  function handleWorkflowDelete() {
    function callback() {
      refWorkflowTable.current?.refresh()
    }

    const isNotLastWorkflow = (workflowData as any).length > 1

    if (isNotLastWorkflow) {
      dispatch(deleteWorkflow(workflowDelete as number, callback))
    } else {
      dispatch(showSnackbar('error', 'You cannot delete the last workflow'))
    }
  }

  function handlePackageDelete() {
    function callback() {
      refPackageTable.current?.refresh()
    }

    dispatch(deletePackage(packageDelete as number, callback))
  }
  
  const workflowTableFilter: Filter[] = [
    {
      key: 'type',
      options: WORKFLOWS_TYPES,
      placeholder: 'Type',
    },
  ]

  const packageTableFilter: Filter[] = [
    {
      key: 'isActive',
      options: PACKAGES_ACTIVE,
      placeholder: 'Active',
    },
    {
      key: 'isAiAssistant',
      options: PACKAGES_INCLUDES_AI,
      placeholder: 'Includes AI',
    },
    {
      key: 'type',
      options: PACKAGES_TYPES,
      placeholder: 'Type',
    },
  ]

  return (
    <PrivatePage>
      <Stack spacing={3} width="100%">  
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="bold" fontSize={26}>
            {t('package.title.lookup')}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={
              <AddIcon
                style={{
                  position: 'relative',
                  right: -5,
                  fontSize: 11,
                }}
              />
            }
            onClick={() => setWorkflowCreateOpen(true)}
            sx={{ paddingLeft: '11px' }}
          >
            {t('package.create.lookup')}
          </Button>
        </Stack>

        <DataTable
          ref={refWorkflowTable}
          id="workflow"
          className={classes.card}
          url="/lookup/admin"
          headerData={tableHeaderLookup}
          renderBody={RenderLookupTableBody}
          quickFilter={['title', 'subTitle']}
          filters={workflowTableFilter}
          collapsible
        />
      </Stack>

      <Stack spacing={3} width="100%">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="bold" fontSize={26}>
            {t('package.title.package')}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={
              <AddIcon
                style={{
                  position: 'relative',
                  right: -5,
                  fontSize: 11,
                }}
              />
            }
            onClick={handlePackageCreate}
            sx={{ paddingLeft: '11px' }}
          >
            {t('package.create.package')}
          </Button>
        </Stack>

        <DataTable
          ref={refPackageTable}
          id="package"
          className={classes.card}
          url="/package/admin"
          headerData={tableHeaderPackage}
          renderBody={RenderPackageTableBody}
          quickFilter={['title', 'subTitle.context']}
          filters={packageTableFilter}
          collapsible
        />
      </Stack>

      {packageCreateOpen && (
        <PopupPackage
          mode="create"
          tableRef={refPackageTable}
          onClose={handleClose}
        />
      )}
      {packageEdit && (
        <PopupPackage
          mode="edit"
          tableRef={refPackageTable}
          data={packageEdit}
          onClose={() => setPackageEdit(null)}
        />
      )}
      {workflowCreateOpen && (
        <PopupWorkflow
          mode="create"
          tableRef={refWorkflowTable}
          onClose={() => setWorkflowCreateOpen(false)}
        />
      )}
      {workflowEdit && (
        <PopupWorkflow
          mode="edit"
          tableRef={refWorkflowTable}
          data={workflowEdit}
          onClose={() => setWorkflowEdit(null)}
        />
      )}
      {workflowDelete && (
        <PopupAsk
          onYes={handleWorkflowDelete}
          onClose={() => setWorkflowDelete(null)}
        >
          {(affectingPackages as Package[])?.length > 0 && (
            <Alert severity="warning">
              <Typography variant="bold" fontSize={22} color="inherit">
                This workflow is using for packages:
              </Typography>
              <ul>
                {(affectingPackages as Package[]).map((el) => (
                  <li key={el.id}>{el.title}</li>
                ))}
              </ul>
            </Alert>
          )}
        </PopupAsk>
      )}
      {packageDelete && (
        <PopupAsk
          onYes={handlePackageDelete}
          onClose={() => setPackageDelete(null)}
        />
      )}
    </PrivatePage>
  )
}
